/**
 * product-custom-fields.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders personalization fields dynamically from Shopify metaobjects.
 * Supports: text, textarea, image/file, number, select, checkbox, date
 *
 * IMAGE UPLOAD BEHAVIOUR (Cloudinary unsigned upload → line item property URL):
 *   - Shopify's cart (/cart/add.js) has NO native file-upload support.
 *     A <input type="file"> submitted with the cart form is silently
 *     ignored by Shopify — it is never stored anywhere.
 *   - So the real <input type="file"> here is used ONLY to let the
 *     customer pick a photo and to drive the local instant preview
 *     (FileReader, never submitted).
 *   - The moment a file is chosen, we upload it directly from the browser
 *     to Cloudinary using an UNSIGNED upload preset (no backend, no
 *     secret key in the client). Cloudinary returns a public HTTPS URL.
 *   - That URL is written into a HIDDEN TEXT INPUT named
 *     properties[<Label>] — THIS is the field that actually gets
 *     submitted with the cart form. The real <input type="file"> is
 *     given no `name` attribute so it is never form-associated and can
 *     never end up in FormData.
 *   - Add to Cart / Buy Now / Addon Drawer are blocked while any image
 *     upload for the current render is still in-flight (PCFGate.isBusy()).
 *
 * Validation behaviour:
 *   - On Add to Cart / Buy Now / Addon Drawer click with empty required fields:
 *       • Red border highlights each empty field
 *       • "This field is required" shown directly below it
 *       • Page scrolls to first empty field
 *       • Cart add / checkout / addon drawer are ALL blocked
 *   - Error clears as soon as the customer fills the field.
 *   - On blur: shows error if field left empty.
 *   - Qty change: renders N sets of fields, preserves filled values.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* ─── Cloudinary config — EDIT THESE TWO VALUES ──────────────────────────── */
window.PCF_CLOUDINARY = window.PCF_CLOUDINARY || {
  cloudName:    'kg2uhgvp', // find on Cloudinary dashboard home / Settings > Account
  uploadPreset: 'wehatke'    // unsigned preset — folder is already pinned to 'shopify' on the preset itself
};

/* ─── Inject styles once ─────────────────────────────────────────────────── */
(function () {
  if (document.getElementById('pcf-styles')) return;
  const s = document.createElement('style');
  s.id = 'pcf-styles';
  s.textContent = `
    /* ── validation states ── */
    .pcf-field-error {
      border-color: #e0342a !important;
      box-shadow: 0 0 0 1.5px #e0342a !important;
      border-radius: 4px;
    }
    .pcf-upload-field-error {
      border-color: #e0342a !important;
      box-shadow: 0 0 0 1.5px #e0342a !important;
    }
    .pcf-required-msg {
      display: flex;
      align-items: center;
      gap: 5px;
      color: #e0342a;
      font-size: 12.5px;
      font-weight: 500;
      margin-top: 5px;
      margin-bottom: 8px;
      line-height: 1.4;
    }
    .pcf-required-msg svg { flex-shrink: 0; }

    /* ── upload status text ── */
    .pcf-upload-status {
      font-size: 11.5px;
      font-weight: 500;
      margin: 4px 0 0;
      display: flex;
      align-items: center;
      gap: 5px;
    }
    .pcf-upload-status.is-uploading { color: #888; }
    .pcf-upload-status.is-success   { color: #1a8a4c; }
    .pcf-upload-status.is-error     { color: #e0342a; }

    .pcf-spinner {
      width: 12px; height: 12px;
      border: 2px solid #ddd;
      border-top-color: #888;
      border-radius: 50%;
      display: inline-block;
      animation: pcf-spin 0.7s linear infinite;
      flex-shrink: 0;
    }
    @keyframes pcf-spin { to { transform: rotate(360deg); } }

    /* ── shake animation ── */
    @keyframes pcf-shake {
      0%,100% { transform: translateX(0); }
      20%      { transform: translateX(-5px); }
      40%      { transform: translateX(5px); }
      60%      { transform: translateX(-3px); }
      80%      { transform: translateX(3px); }
    }
    .pcf-shake { animation: pcf-shake 0.3s ease; }

    /* ── field cards ── */
    .gift-wrap { margin-bottom: 20px; }
    .gift-header { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
    .gift-header h3 { font-size: 18px; font-weight: 500; margin: 0; }
    .gift-icon { color: #e61a6f; font-size: 22px; }
    .gift-sub { font-size: 13px; color: #888; margin: 0 0 16px; }

    .pcf-item-group { border: 1.5px solid #eee; border-radius: 16px; padding: 16px 18px; margin-bottom: 16px; }
    .pcf-item-title { font-size: 13px; font-weight: 600; color: #e61a6f; margin: 0 0 12px; text-transform: uppercase; letter-spacing: 0.5px; }

    .pcf-card { border: 1.5px dashed #ddd; border-radius: 16px; padding: 16px 18px; margin-bottom: 12px; background: #fff; transition: border-color 0.2s, box-shadow 0.2s; }
    .pcf-card.pcf-upload-field-error { border-style: solid; }

    .pcf-upload-inner { display: flex; align-items: center; gap: 16px; cursor: pointer; }
    .pcf-upload-inner.is-disabled { cursor: not-allowed; opacity: 0.7; }
    .pcf-upload-icon {
      width: 60px; height: 60px; min-width: 60px; border-radius: 12px;
      background: #fce8e8; display: flex; align-items: center;
      justify-content: center; flex-shrink: 0; overflow: hidden;
      position: relative;
    }
    .pcf-upload-title { font-size: 15px; font-weight: 500; margin: 0 0 2px; }
    .pcf-upload-sub { font-size: 12px; color: #999; margin: 0; }
    .pcf-upload-fname { font-size: 11px; color: #666; margin: 4px 0 0; word-break: break-all; }

    /* ── image preview & remove ── */
    .pcf-preview-wrap {
      position: relative;
      width: 60px;
      height: 60px;
      flex-shrink: 0;
    }
    .pcf-preview-wrap img {
      width: 60px;
      height: 60px;
      object-fit: cover;
      border-radius: 12px;
      display: block;
    }
    .pcf-remove-btn {
      position: absolute;
      top: -6px;
      right: -6px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #333;
      color: #fff;
      border: 2px solid #fff;
      font-size: 12px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      padding: 0;
      box-shadow: 0 1px 4px rgba(0,0,0,0.25);
      transition: background 0.15s;
    }
    .pcf-remove-btn:hover { background: #e0342a; }

    .pcf-label { font-size: 14px; font-weight: 500; margin: 0 0 10px; }

    .pcf-textarea {
      width: 100%; min-height: 100px; border: none; background: transparent;
      resize: vertical; font-size: 13px; outline: none;
      box-sizing: border-box; padding: 0; font-family: inherit; color: inherit;
    }
    .pcf-textarea::placeholder { color: #bbb; }

    .pcf-input {
      width: 100%; border: none; background: transparent; font-size: 13px;
      outline: none; box-sizing: border-box; padding: 4px 0;
      font-family: inherit; color: inherit; height: 36px;
    }
    .pcf-input::placeholder { color: #bbb; }

    .pcf-select {
      width: 100%; border: none; background: transparent;
      font-size: 13px; outline: none; font-family: inherit; padding: 4px 0;
    }

    .pcf-counter { text-align: right; font-size: 11px; color: #bbb; border-top: 1px solid #f0f0f0; padding-top: 8px; margin-top: 8px; }
  `;
  document.head.appendChild(s);
})();


/* ─── Global validation gate ─────────────────────────────────────────────── */
window.PCFGate = window.PCFGate || {
  _instance: null,
  register(instance) { this._instance = instance; },
  validate() {
    if (!this._instance) return true;
    return this._instance._validateAll();
  },
  isBusy() {
    if (!this._instance) return false;
    return this._instance._uploadsInFlight > 0;
  },
  /* No-op kept for backward compatibility; nothing to inject anymore —
     the hidden URL text inputs are already form-associated. */
  injectInto(form) {
    return;
  }
};

/* ─── Early capture listener (runs before addon-drawer.js and theme AJAX cart) ── */
document.addEventListener('click', function pcfGlobalGuard(e) {
  if (e.target.closest('#addon-drawer')) return;

  const isATC = e.target.closest(
    'button[name="add"], [type="submit"][name="add"]'
  );
  const isBuyNow = e.target.closest(
    '.shopify-payment-button__button, [data-testid="Checkout-button"], .shopify-payment-button button'
  );

  if (!isATC && !isBuyNow) return;

  if (window.PCFGate.isBusy()) {
    e.preventDefault();
    e.stopImmediatePropagation();
    window.PCFGate._instance?._flashUploadingNotice();
    return;
  }

  if (!window.PCFGate.validate()) {
    e.preventDefault();
    e.stopImmediatePropagation();
    return;
  }
}, true);


/* ─── Custom Element ─────────────────────────────────────────────────────── */
class ProductCustomFields extends HTMLElement {

  /* ── lifecycle ── */

  connectedCallback() {
    this._qty              = 1;
    this._fields           = [];
    this._saved            = {};   // text/select/checkbox/date values, keyed by fi_unit
    this._savedImageUrls   = {};   // Cloudinary URLs, keyed by fi_unit — survive qty re-renders
    this._qtyInput         = null;
    this._observer         = null;
    this._retries          = 0;
    this._form             = null;
    this._uploadsInFlight  = 0;
    this._pendingQty       = null;

    try {
      this._fields = JSON.parse(this.dataset.fields || '[]');
    } catch (e) {
      console.warn('[product-custom-fields] Bad data-fields JSON:', e);
    }

    if (!this._fields.length) return;

    this._container = this.querySelector('.product-custom-fields__sets');
    if (!this._container) return;

    window.PCFGate.register(this);
    this._render();
    this._bindQtyInput();
  }

  disconnectedCallback() {
    this._teardown();
  }

  /* ── qty binding ── */

  _bindQtyInput() {
    const form =
      this.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!form) { this._retry(); return; }
    this._form = form;

    /* Hidden URL inputs (and every other field) must physically live inside
       the form element that gets submitted — FormData only reads
       form-associated fields. */
    this._ensureContainerInsideForm(form);

    const qtyInput =
      form.querySelector('input[name="quantity"]') ||
      form.querySelector('input[type="number"][id*="Quantity"]') ||
      form.querySelector('input[type="number"][id*="quantity"]') ||
      form.querySelector('quantity-input input') ||
      form.querySelector('.quantity__input');

    if (!qtyInput) { this._retry(); return; }

    this._qtyInput = qtyInput;
    this._qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
    this._render();

    this._handleQtyChange = () => {
      const newQty = Math.max(1, parseInt(this._qtyInput.value, 10) || 1);
      this._changeQty(newQty);
    };

    qtyInput.addEventListener('input',  this._handleQtyChange);
    qtyInput.addEventListener('change', this._handleQtyChange);

    /* custom qty:changed event (pill or other widgets) */
    document.addEventListener('quantity:changed', this._onCustomQtyEvent = (e) => {
      if (!e.detail) return;
      const newQty = Math.max(1, parseInt(e.detail.qty ?? e.detail.quantity, 10) || 1);
      this._changeQty(newQty);
    });

    /* MutationObserver fallback for qty pill (.qpm-count) */
    const pillCount = document.querySelector('.qpm-count');
    if (pillCount) {
      new MutationObserver(() => {
        const newQty = Math.max(1, parseInt(pillCount.textContent, 10) || 1);
        this._changeQty(newQty);
      }).observe(pillCount, { childList: true, characterData: true, subtree: true });
    }

    /* MutationObserver on the qty input value attribute */
    this._observer = new MutationObserver(() => {
      const newQty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      this._changeQty(newQty);
    });
    this._observer.observe(qtyInput, { attributes: true, attributeFilter: ['value'] });
  }

  /**
   * Single funnel for every qty-change source (native input, qty pill,
   * custom event, attribute mutation). Multiple listeners can legitimately
   * fire for the SAME user action (e.g. the qty pill updates its own
   * .qpm-count text AND dispatches change/input on the real quantity
   * input in one click) — without this guard, _saveCurrentValues()/_render()
   * could run twice back-to-back and risk clobbering data mid-update.
   * This collapses same-tick duplicate calls into a single render pass.
   */
  _changeQty(newQty) {
    if (newQty === this._qty) return;          // already at target, no-op
    if (this._pendingQty === newQty) return;    // a render for this qty is already queued
    this._pendingQty = newQty;
    this._saveCurrentValues();
    this._qty = newQty;
    this._render();
    this._pendingQty = null;
  }

  _retry() {
    if (this._retries < 10) {
      this._retries++;
      setTimeout(() => this._bindQtyInput(), 300);
    }
  }

  _ensureContainerInsideForm(form) {
    if (!this._container) return;
    if (form.contains(this._container)) return; // already inside, nothing to do
    form.appendChild(this._container);
  }

  _teardown() {
    if (this._qtyInput && this._handleQtyChange) {
      this._qtyInput.removeEventListener('input',  this._handleQtyChange);
      this._qtyInput.removeEventListener('change', this._handleQtyChange);
    }
    if (this._onCustomQtyEvent) {
      document.removeEventListener('quantity:changed', this._onCustomQtyEvent);
    }
    if (this._observer) this._observer.disconnect();
  }

  /* ── validation ── */

  _validateAll() {
    if (!this._container) return true;

    let allValid     = true;
    let firstInvalid = null;

    /* text / textarea / number / select / date / checkbox + the hidden
       url input that stands in for each image field — the image-picker
       itself is deliberately excluded since it shares data-fi/data-unit
       with its hidden URL sibling and would otherwise be validated twice,
       producing two "This field is required" messages on one image card */
    this._container.querySelectorAll('[data-fi][data-unit]:not([data-kind="image-picker"])').forEach((el) => {
      if (!this._isRequired(el)) return;

      if (!this._hasValue(el)) {
        this._markError(el);
        this._shake(el);
        if (!firstInvalid) firstInvalid = el;
        allValid = false;
      } else {
        this._clearError(el);
      }
    });

    if (firstInvalid) {
      const scrollTarget = firstInvalid.dataset.kind === 'image'
        ? (firstInvalid.closest('.pcf-card') || firstInvalid)
        : firstInvalid;
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return allValid;
  }

  _isRequired(el) {
    return el.dataset.required === 'true';
  }

  _hasValue(el) {
    if (el.dataset.kind === 'image') return el.value.trim() !== ''; // hidden URL input
    if (el.type === 'checkbox')      return el.checked;
    return el.value.trim() !== '';
  }

  _markError(el) {
    const msgId = `pcf-msg-${el.id}`;
    if (document.getElementById(msgId)) return;

    if (el.dataset.kind === 'image') {
      const card = el.closest('.pcf-card');
      if (card) {
        card.classList.add('pcf-upload-field-error');
        card.insertAdjacentHTML('beforeend', this._msgHTML(msgId));
      }
    } else {
      el.classList.add('pcf-field-error');
      el.insertAdjacentHTML('afterend', this._msgHTML(msgId));
    }
  }

  _msgHTML(id) {
    return `
      <span id="${id}" class="pcf-required-msg" role="alert">
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle cx="6.5" cy="6.5" r="6.5" fill="#e0342a"/>
          <path d="M6.5 3.5v3.25" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/>
          <circle cx="6.5" cy="9.25" r="0.75" fill="#fff"/>
        </svg>
        This field is required
      </span>`;
  }

  _clearError(el) {
    const msgId = `pcf-msg-${el.id}`;
    document.getElementById(msgId)?.remove();
    if (el.dataset.kind === 'image') {
      el.closest('.pcf-card')?.classList.remove('pcf-upload-field-error');
    } else {
      el.classList.remove('pcf-field-error');
    }
  }

  _shake(el) {
    const target = el.dataset.kind === 'image'
      ? (el.closest('.pcf-card') || el)
      : el;
    target.classList.remove('pcf-shake');
    void target.offsetWidth;
    target.classList.add('pcf-shake');
    target.addEventListener('animationend', () => target.classList.remove('pcf-shake'), { once: true });
  }

  _flashUploadingNotice() {
    /* Briefly shake every card that still has an upload in flight, so the
       customer understands why Add to Cart didn't go through. */
    this._container?.querySelectorAll('.pcf-upload-status.is-uploading').forEach((statusEl) => {
      const card = statusEl.closest('.pcf-card');
      if (card) this._shake({ dataset: { kind: 'image' }, closest: () => card });
    });
  }

  /* ── value preservation ── */

  _saveCurrentValues() {
    if (!this._container) return;
    this._container.querySelectorAll('[data-fi][data-unit]').forEach((el) => {
      const key = `${el.dataset.fi}_${el.dataset.unit}`;
      if (el.dataset.kind === 'image') return; // handled separately below
      if (el.type === 'checkbox') this._saved[key] = el.checked;
      else this._saved[key] = el.value;
    });

    /* Persist Cloudinary URLs (and filenames) across qty re-renders so an
       already-uploaded photo for "Item 1" isn't lost when qty changes. */
    this._container.querySelectorAll('input[type="hidden"][data-kind="image"]').forEach((el) => {
      const key = `${el.dataset.fi}_${el.dataset.unit}`;
      if (el.value) {
        this._savedImageUrls[key] = {
          url: el.value,
          fname: document.getElementById(`${el.id}_fname`)?.textContent || ''
        };
      }
    });
  }

  _restoreValues() {
    if (!this._container) return;
    this._container.querySelectorAll('[data-fi][data-unit]').forEach((el) => {
      if (el.dataset.kind === 'image') return;
      const key = `${el.dataset.fi}_${el.dataset.unit}`;
      if (this._saved[key] === undefined) return;
      if (el.type === 'checkbox') el.checked = this._saved[key];
      else el.value = this._saved[key];
    });
  }

  _restoreImageUrls() {
    Object.keys(this._savedImageUrls).forEach((key) => {
      const [fi, unit] = key.split('_');
      const hidden = this._container.querySelector(
        `input[type="hidden"][data-kind="image"][data-fi="${fi}"][data-unit="${unit}"]`
      );
      if (!hidden) return;
      const saved = this._savedImageUrls[key];
      hidden.value = saved.url;

      const baseId    = hidden.id.replace(/_url$/, '');
      const fnameEl   = document.getElementById(`${baseId}_fname`);
      const previewEl = document.getElementById(`${baseId}_preview`);
      const wrapEl    = document.getElementById(`${baseId}_previewWrap`);
      const iconEl    = document.getElementById(`${baseId}_icon`);
      const statusEl  = document.getElementById(`${baseId}_status`);

      if (fnameEl) fnameEl.textContent = saved.fname;
      if (previewEl) previewEl.src = saved.url;
      if (wrapEl) wrapEl.style.display = 'block';
      if (iconEl) iconEl.style.display = 'none';
      if (statusEl) statusEl.innerHTML = this._statusHTML('success', 'Uploaded');
    });
  }

  /* ── rendering ── */

  _render() {
    if (!this._container) return;

    this._container.innerHTML = Array.from(
      { length: this._qty },
      (_, i) => this._renderGroup(i + 1)
    ).join('');

    this._restoreValues();
    this._restoreImageUrls();
    this._bindFieldEvents();
  }

  _propName(baseLabel, unit) {
    return this._qty === 1 ? baseLabel : `${baseLabel} - Item ${unit}`;
  }

  _renderGroup(unit) {
    let inner = '';
    this._fields.forEach((field, fi) => {
      const type = (field.type || '').toLowerCase().trim();
      if      (type === 'text')                                          inner += this._renderText(field, fi, unit);
      else if (type === 'textarea')                                      inner += this._renderTextarea(field, fi, unit);
      else if (type === 'image' || type === 'file' || type === 'upload') inner += this._renderImage(field, fi, unit);
      else if (type === 'number')                                        inner += this._renderNumber(field, fi, unit);
      else if (type === 'select' || type === 'dropdown')                 inner += this._renderSelect(field, fi, unit);
      else if (type === 'checkbox')                                      inner += this._renderCheckbox(field, fi, unit);
      else if (type === 'date')                                          inner += this._renderDate(field, fi, unit);
      else                                                               inner += this._renderText(field, fi, unit);
    });

    return `
    <div class="gift-wrap mb-20 mt-20">
    <div class="gift-header">
      <span class="gift-icon">✦</span>
      <h3>Personalize your gift</h3>
    </div>
    <p class="gift-sub">Add a photo and a custom message for each item.</p>
      <div class="pcf-item-group" data-unit="${unit}">
        ${this._qty > 1 ? `<p class="pcf-item-title">Item ${unit}</p>` : ''}
        ${inner}
      </div>`;
  }

  /* ── individual field renderers ── */

  _renderText(field, fi, unit) {
    const id       = `pcf-text-${fi}-${unit}`;
    const propName = this._propName(field.label, unit);
    const counter  = field.maxlen
      ? `<div class="pcf-counter"><span id="${id}_c">0/${field.maxlen}</span></div>` : '';
    return `
      <div class="pcf-card">
        <p class="pcf-label">${this._esc(field.label)}${field.required ? ' <span style="color:#e61a6f">*</span>' : ''}</p>
        <input
          id="${id}" type="text"
          class="pcf-input"
          name="properties[${this._esc(propName)}]"
          placeholder="${this._esc(field.ph || '')}"
          ${field.maxlen ? `maxlength="${field.maxlen}"` : ''}
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}"
          autocomplete="off"
        >
        ${counter}
      </div>`;
  }

  _renderTextarea(field, fi, unit) {
    const id       = `pcf-ta-${fi}-${unit}`;
    const propName = this._propName(field.label, unit);
    const counter  = field.maxlen
      ? `<div class="pcf-counter"><span id="${id}_c">0/${field.maxlen}</span></div>` : '';
    return `
      <div class="pcf-card">
        <p class="pcf-label">${this._esc(field.label)}${field.required ? ' <span style="color:#e61a6f">*</span>' : ''}</p>
        <textarea
          id="${id}"
          class="pcf-textarea"
          name="properties[${this._esc(propName)}]"
          placeholder="${this._esc(field.ph || '')}"
          ${field.maxlen ? `maxlength="${field.maxlen}"` : ''}
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}"
        ></textarea>
        ${counter}
      </div>`;
  }

  /**
   * Image field — uploads to Cloudinary, line item property becomes a URL.
   *
   *  - <input type="file" id="..."> has NO name attribute → never
   *    form-associated → never appears in FormData → Shopify never sees it.
   *    It exists purely so the customer can pick a file and we get a
   *    local FileReader preview.
   *  - <input type="hidden" id="..._url" name="properties[Label]"> is the
   *    field that actually gets submitted. Its value is set to the
   *    Cloudinary secure_url once the upload finishes.
   */
  _renderImage(field, fi, unit) {
    const id        = `pcf-img-${fi}-${unit}`;
    const propName  = this._propName(field.label, unit);
    return `
      <div class="pcf-card" id="${id}_card">
        <div class="pcf-upload-inner" id="${id}_trigger">

          <!-- Default icon (shown before a file is chosen) -->
          <div class="pcf-upload-icon" id="${id}_icon">
            <svg id="${id}_svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#e61a6f" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0L8 8m4-4l4 4"/>
            </svg>
          </div>

          <!-- Preview thumbnail + remove button (shown after a file is chosen) -->
          <div class="pcf-preview-wrap" id="${id}_previewWrap" style="display:none;">
            <img id="${id}_preview" src="" alt="preview">
            <button
              type="button"
              class="pcf-remove-btn"
              id="${id}_removeBtn"
              aria-label="Remove image"
            >&#x2715;</button>
          </div>

          <div style="flex:1">
            <p class="pcf-upload-title">${this._esc(field.label)}${field.required ? ' <span style="color:#e61a6f">*</span>' : ''}</p>
            <p class="pcf-upload-sub" id="${id}_sub">JPG or PNG · up to ${field.maxsize || 20}MB</p>
            <p class="pcf-upload-fname" id="${id}_fname"></p>
            <p class="pcf-upload-status" id="${id}_status"></p>
          </div>
        </div>

        <!-- Picker only — deliberately has NO name attribute, never submitted -->
        <input
          id="${id}" type="file"
          accept="image/*"
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}" data-kind="image-picker"
          style="display:none"
        >

        <!-- Actually submitted field: Cloudinary URL goes here -->
        <input
          id="${id}_url" type="hidden"
          name="properties[${this._esc(propName)}]"
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}" data-kind="image"
          value=""
        >
      </div>`;
  }

  _renderNumber(field, fi, unit) {
    const id       = `pcf-num-${fi}-${unit}`;
    const propName = this._propName(field.label, unit);
    return `
      <div class="pcf-card">
        <p class="pcf-label">${this._esc(field.label)}${field.required ? ' <span style="color:#e61a6f">*</span>' : ''}</p>
        <input
          id="${id}" type="number"
          class="pcf-input"
          name="properties[${this._esc(propName)}]"
          placeholder="${this._esc(field.ph || '')}"
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}"
        >
      </div>`;
  }

  _renderSelect(field, fi, unit) {
    const id       = `pcf-sel-${fi}-${unit}`;
    const propName = this._propName(field.label, unit);
    const opts     = (field.options || [])
      .map(o => `<option value="${this._esc(o)}">${this._esc(o)}</option>`)
      .join('');
    return `
      <div class="pcf-card">
        <p class="pcf-label">${this._esc(field.label)}${field.required ? ' <span style="color:#e61a6f">*</span>' : ''}</p>
        <select
          id="${id}"
          class="pcf-select"
          name="properties[${this._esc(propName)}]"
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}"
        >
          <option value="">-- ${this._esc(field.ph || 'Select option')} --</option>
          ${opts}
        </select>
      </div>`;
  }

  _renderCheckbox(field, fi, unit) {
    const id       = `pcf-chk-${fi}-${unit}`;
    const propName = this._propName(field.label, unit);
    return `
      <div class="pcf-card" style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        <input
          id="${id}" type="checkbox"
          name="properties[${this._esc(propName)}]"
          value="Yes"
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}"
          style="width:18px;height:18px;cursor:pointer;flex-shrink:0;"
        >
        <label for="${id}" style="font-size:14px;font-weight:500;cursor:pointer;">
          ${this._esc(field.label)}${field.required ? ' <span style="color:#e61a6f">*</span>' : ''}
        </label>
      </div>`;
  }

  _renderDate(field, fi, unit) {
    const id       = `pcf-date-${fi}-${unit}`;
    const propName = this._propName(field.label, unit);
    return `
      <div class="pcf-card">
        <p class="pcf-label">${this._esc(field.label)}${field.required ? ' <span style="color:#e61a6f">*</span>' : ''}</p>
        <input
          id="${id}" type="date"
          class="pcf-input"
          name="properties[${this._esc(propName)}]"
          data-fi="${fi}" data-unit="${unit}" data-required="${field.required}"
        >
      </div>`;
  }

  /* ── live event binding ── */

  _bindFieldEvents() {
    if (!this._container) return;

    /* text + number inputs */
    this._container.querySelectorAll('input[type="text"][data-fi], input[type="number"][data-fi]').forEach((el) => {
      el.addEventListener('input', () => {
        if (this._hasValue(el)) this._clearError(el);
        this._updateCounter(el);
      });
      el.addEventListener('blur', () => {
        if (this._isRequired(el) && !this._hasValue(el)) this._markError(el);
        else this._clearError(el);
      });
    });

    /* textarea */
    this._container.querySelectorAll('textarea[data-fi]').forEach((el) => {
      el.addEventListener('input', () => {
        if (this._hasValue(el)) this._clearError(el);
        this._updateCounter(el);
      });
      el.addEventListener('blur', () => {
        if (this._isRequired(el) && !this._hasValue(el)) this._markError(el);
        else this._clearError(el);
      });
    });

    /* image picker → Cloudinary upload */
    this._container.querySelectorAll('input[type="file"][data-kind="image-picker"]').forEach((el) => {
      const id      = el.id;
      const trigger = document.getElementById(`${id}_trigger`);

      if (trigger) {
        trigger.addEventListener('click', () => {
          if (this._uploadsInFlight > 0 && trigger.classList.contains('is-disabled')) return;
          el.click();
        });
      }

      el.addEventListener('change', () => {
        const file = el.files?.[0];
        if (!file) return;

        const fname = document.getElementById(`${id}_fname`);
        if (fname) fname.textContent = file.name;

        this._showLocalPreview(id, file);
        this._uploadToCloudinary(id, file);
      });

      const removeBtn = document.getElementById(`${id}_removeBtn`);
      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._clearPreview(id);
          const hidden = document.getElementById(`${id}_url`);
          if (hidden && this._isRequired(hidden)) this._markError(hidden);
        });
      }
    });

    /* select + date */
    this._container.querySelectorAll('select[data-fi], input[type="date"][data-fi]').forEach((el) => {
      el.addEventListener('change', () => {
        if (this._hasValue(el)) this._clearError(el);
        else if (this._isRequired(el)) this._markError(el);
      });
    });

    /* checkbox */
    this._container.querySelectorAll('input[type="checkbox"][data-fi]').forEach((el) => {
      el.addEventListener('change', () => {
        if (this._hasValue(el)) this._clearError(el);
        else if (this._isRequired(el)) this._markError(el);
      });
    });
  }

  /* ── Cloudinary upload ─────────────────────────────────────────────── */

  _uploadToCloudinary(fileInputId, file) {
    const { cloudName, uploadPreset } = window.PCF_CLOUDINARY;

    if (!cloudName || cloudName === 'YOUR_CLOUD_NAME' || !uploadPreset || uploadPreset === 'YOUR_UPLOAD_PRESET') {
      console.error('[product-custom-fields] Cloudinary not configured — set window.PCF_CLOUDINARY.cloudName / uploadPreset.');
      this._setStatus(fileInputId, 'error', 'Upload not configured');
      return;
    }

    const trigger    = document.getElementById(`${fileInputId}_trigger`);
    const hiddenInput = document.getElementById(`${fileInputId}_url`);

    this._uploadsInFlight++;
    if (trigger) trigger.classList.add('is-disabled');
    this._setStatus(fileInputId, 'uploading', 'Uploading…');
    if (hiddenInput) hiddenInput.value = ''; // clear any previous URL while re-uploading

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset);

    fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
      method: 'POST',
      body: formData
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Cloudinary responded ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!data.secure_url) throw new Error('No secure_url in Cloudinary response');
        if (hiddenInput) {
          hiddenInput.value = data.secure_url;
          this._clearError(hiddenInput);
        }
        this._setStatus(fileInputId, 'success', 'Uploaded');
      })
      .catch((err) => {
        console.error('[product-custom-fields] Cloudinary upload failed:', err);
        this._setStatus(fileInputId, 'error', 'Upload failed — tap to retry');
        if (hiddenInput) hiddenInput.value = '';
      })
      .finally(() => {
        this._uploadsInFlight = Math.max(0, this._uploadsInFlight - 1);
        if (trigger) trigger.classList.remove('is-disabled');
      });
  }

  _statusHTML(state, text) {
    const icon = state === 'uploading'
      ? '<span class="pcf-spinner"></span>'
      : state === 'success'
        ? '✓'
        : '⚠';
    return `${icon} ${this._esc(text)}`;
  }

  _setStatus(fileInputId, state, text) {
    const el = document.getElementById(`${fileInputId}_status`);
    if (!el) return;
    el.className = `pcf-upload-status is-${state}`;
    el.innerHTML = this._statusHTML(state, text);
  }

  /* ── local preview only (never submitted) ──────────────────────────── */

  _showLocalPreview(fileInputId, file) {
    const preview     = document.getElementById(`${fileInputId}_preview`);
    const previewWrap = document.getElementById(`${fileInputId}_previewWrap`);
    const iconWrap     = document.getElementById(`${fileInputId}_icon`);

    const reader = new FileReader();
    reader.onload = (ev) => {
      if (preview)     preview.src = ev.target.result;
      if (previewWrap) previewWrap.style.display = 'block';
      if (iconWrap)     iconWrap.style.display = 'none';
    };
    reader.readAsDataURL(file);
  }

  _clearPreview(fileInputId) {
    const fileInput    = document.getElementById(fileInputId);
    const hiddenInput  = document.getElementById(`${fileInputId}_url`);
    const preview      = document.getElementById(`${fileInputId}_preview`);
    const previewWrap  = document.getElementById(`${fileInputId}_previewWrap`);
    const icon         = document.getElementById(`${fileInputId}_icon`);
    const fname        = document.getElementById(`${fileInputId}_fname`);
    const status       = document.getElementById(`${fileInputId}_status`);

    if (fileInput)   fileInput.value = '';
    if (hiddenInput) hiddenInput.value = '';
    if (preview)     preview.src = '';
    if (previewWrap) previewWrap.style.display = 'none';
    if (icon)         icon.style.display = 'flex';
    if (fname)        fname.textContent = '';
    if (status)       { status.innerHTML = ''; status.className = 'pcf-upload-status'; }
  }

  /* ── counter ── */

  _updateCounter(el) {
    const counter = document.getElementById(`${el.id}_c`);
    if (counter && el.maxLength > 0) {
      counter.textContent = `${el.value.length}/${el.maxLength}`;
    }
  }

  /* ── helpers ── */

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get('product-custom-fields')) {
  customElements.define('product-custom-fields', ProductCustomFields);
}